import { useState } from "react";
import { useReveal } from "@/hooks/useReveal";

interface Project {
  id: number;
  name: string;
  industry: string;
  duration: string;
  description: string;
  technologies: string[];
  image_url: string;
}

const projects: Project[] = [
  {
    id: 1,
    name: "FinTrack Pro",
    industry: "Fintech",
    duration: "12 weeks",
    description: "A comprehensive financial tracking platform for SMEs with real-time analytics, automated reporting, and multi-currency support.",
    technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
    image_url: "https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/1f6c915eb2b15ab6b6622b8ec7e64f2b.png",
  },
  {
    id: 2,
    name: "HealthConnect",
    industry: "Healthcare",
    duration: "16 weeks",
    description: "Patient management system with telemedicine features, appointment scheduling, and secure electronic health records.",
    technologies: ["Next.js", "Python", "FHIR API", "Docker"],
    image_url: "https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/9d43394f8c0b8efeddb4676731f7746a.png",
  },
  {
    id: 3,
    name: "RetailSync",
    industry: "E-Commerce",
    duration: "10 weeks",
    description: "Omnichannel retail platform integrating inventory management, POS systems, and customer loyalty programs.",
    technologies: ["Vue.js", "Go", "Redis", "Kubernetes"],
    image_url: "https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/3817251f066642bf1b6946d09f623a18.png",
  },
  {
    id: 4,
    name: "EduLearn Portal",
    industry: "EdTech",
    duration: "14 weeks",
    description: "Interactive learning management system with video courses, progress tracking, and AI-powered personalized recommendations.",
    technologies: ["React", "Django", "TensorFlow", "GCP"],
    image_url: "https://static.readdy.ai/image/b83d215482d53eb69b064b5c4f8c0569/9e89f17f441c229ae24070ce02345558.png",
  },
];

export default function Transformations() {
  const ref = useReveal<HTMLDivElement>();
  const [active, setActive] = useState(0);

  const current = projects[active];

  return (
    <section ref={ref} id="products" className="bg-cream py-24 md:py-32 border-t border-ink/5">
      <div className="mx-auto max-w-[1320px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7" data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-ink/60">Selected work</span>
            <h2 className="mt-4 text-display text-4xl md:text-6xl leading-[1.05] text-balance">
              Projects that <span className="italic font-light">speak for themselves.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pl-12" data-reveal data-reveal-delay="120">
            <p className="text-ink/70 text-base md:text-lg leading-relaxed">
              From startups to established enterprises, we deliver solutions that solve real problems
              and create lasting business value. Each project is a testament to our commitment to excellence.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch" data-reveal>
          {/* Project selector */}
          <div className="lg:col-span-4 space-y-3">
            {projects.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setActive(i)}
                className={`w-full text-left rounded-2xl p-5 transition-all duration-300 cursor-pointer border ${
                  i === active
                    ? "bg-ink text-cream border-ink"
                    : "bg-cream border-ink/5 hover:border-ink/15 text-ink"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-display text-lg">{p.name}</div>
                    <div className={`text-xs mt-1 ${i === active ? "text-cream/50" : "text-ink/50"}`}>
                      {p.industry} · {p.duration}
                    </div>
                  </div>
                  <span
                    className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${
                      i === active ? "bg-lime-accent text-ink" : "bg-ink/5 text-ink/40"
                    }`}
                  >
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Project detail */}
          <div className="lg:col-span-8">
            <div className="relative h-[480px] md:h-[560px] rounded-3xl overflow-hidden bg-ink/[0.03]">
              <img
                src={current.image_url}
                alt={current.name}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 text-cream">
                <div className="flex flex-wrap gap-2 mb-4">
                  {current.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="rounded-full bg-cream/20 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-widest2 font-semibold"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
                <p className="text-cream/90 text-base md:text-lg leading-relaxed max-w-xl">
                  {current.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}